import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';

const HotelMap = ({ 
  hotels = [], 
  nearbyHotels = [],
  selectedHotelId = null,
  onHotelSelect = null,
  onHotelHover = null,
  height = '300px',
  className = '',
  showInfoWindows = true,
  userLocation = null
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const overlaysRef = useRef([]);
  const nearbyMarkersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const selectedIdRef = useRef(selectedHotelId);

  // Keep ref in sync
  selectedIdRef.current = selectedHotelId;

  const validHotels = hotels.filter(h => h.latitude && h.longitude && parseFloat(h.latitude) !== 0);
  const validNearby = nearbyHotels.filter(h => h.latitude && h.longitude && parseFloat(h.latitude) !== 0);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || validHotels.length === 0) return;
    let cancelled = false;

    loadGoogleMaps().then(() => {
      if (cancelled || !mapRef.current || !window.google?.maps) return;

      const map = new window.google.maps.Map(mapRef.current, {
        zoom: 12,
        center: { lat: parseFloat(validHotels[0].latitude), lng: parseFloat(validHotels[0].longitude) },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] }
        ]
      });

      mapInstanceRef.current = map;
      infoWindowRef.current = new window.google.maps.InfoWindow();
      setMapReady(true);
    });

    return () => {
      cancelled = true;
      overlaysRef.current.forEach(o => o.setMap(null));
      nearbyMarkersRef.current.forEach(m => m.setMap(null));
      if (userMarkerRef.current) userMarkerRef.current.setMap(null);
      overlaysRef.current = [];
      nearbyMarkersRef.current = [];
      userMarkerRef.current = null;
    };
  }, [validHotels.length === 0]);

  // Create PriceLabel overlay class
  useEffect(() => {
    if (!mapInstanceRef.current || !mapReady) return;
    const map = mapInstanceRef.current;

    // Clear old overlays
    overlaysRef.current.forEach(o => o.setMap(null));
    nearbyMarkersRef.current.forEach(m => m.setMap(null));
    overlaysRef.current = [];
    nearbyMarkersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();

    // --- Price Label Overlay Class ---
    class PriceLabelOverlay extends window.google.maps.OverlayView {
      constructor(position, hotel, opts) {
        super();
        this.position = position;
        this.hotel = hotel;
        this.div = null;
        this.opts = opts;
      }

      onAdd() {
        this.div = document.createElement('div');
        const price = this.hotel.min_price ? `€${Math.round(this.hotel.min_price)}` : '•';
        const isSelected = this.hotel.hotel_id === selectedIdRef.current;

        Object.assign(this.div.style, {
          position: 'absolute',
          cursor: 'pointer',
          zIndex: isSelected ? '100' : '10',
          transform: 'translate(-50%, -50%)',
          transition: 'transform 0.15s ease, z-index 0s'
        });

        this.div.innerHTML = `<div class="price-marker ${isSelected ? 'price-marker--active' : ''}" data-hotel-id="${this.hotel.hotel_id}">${price}</div>`;
        this.div.firstChild.addEventListener('click', () => {
          if (this.opts.onClick) this.opts.onClick(this.hotel);
        });
        this.div.firstChild.addEventListener('mouseenter', () => {
          if (this.opts.onHover) this.opts.onHover(this.hotel.hotel_id);
          this.setActive(true);
        });
        this.div.firstChild.addEventListener('mouseleave', () => {
          if (this.opts.onHover) this.opts.onHover(null);
          if (this.hotel.hotel_id !== selectedIdRef.current) {
            this.setActive(false);
          }
        });

        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(this.div);
      }

      draw() {
        if (!this.div) return;
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(this.position);
        if (point) {
          this.div.style.left = point.x + 'px';
          this.div.style.top = point.y + 'px';
        }
      }

      onRemove() {
        if (this.div?.parentNode) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }

      setActive(active) {
        if (!this.div?.firstChild) return;
        const el = this.div.firstChild;
        if (active) {
          el.classList.add('price-marker--active');
          this.div.style.zIndex = '100';
        } else {
          el.classList.remove('price-marker--active');
          this.div.style.zIndex = '10';
        }
      }

      updateSelected(selId) {
        if (!this.div?.firstChild) return;
        const isNowSelected = this.hotel.hotel_id === selId;
        this.setActive(isNowSelected);
      }
    }

    // Create price label overlays for main hotels
    validHotels.forEach(hotel => {
      const lat = parseFloat(hotel.latitude);
      const lng = parseFloat(hotel.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      const position = new window.google.maps.LatLng(lat, lng);
      const overlay = new PriceLabelOverlay(position, hotel, {
        onClick: (h) => {
          if (showInfoWindows && infoWindowRef.current) {
            const price = h.min_price ? `€${Math.round(h.min_price)}` : '';
            const stars = h.star_rating ? '★'.repeat(h.star_rating) : '';
            const img = h.image_url || h.images?.[0] || '';
            infoWindowRef.current.setContent(`
              <div style="display:flex;gap:10px;padding:8px 4px;max-width:300px;font-family:system-ui,-apple-system,sans-serif">
                ${img ? `<img src="${img}" alt="" style="width:72px;height:72px;border-radius:8px;object-fit:cover;flex-shrink:0" onerror="this.style.display='none'" />` : ''}
                <div style="min-width:0;flex:1">
                  <div style="font-weight:700;font-size:14px;margin-bottom:3px;color:#111;line-height:1.3">${h.name}</div>
                  <div style="font-size:11px;color:#666;margin-bottom:5px">${stars} ${h.city || ''}</div>
                  ${price ? `<div style="font-size:15px;font-weight:700;color:#2563eb">${price}</div>` : ''}
                </div>
              </div>
            `);
            infoWindowRef.current.setPosition(position);
            infoWindowRef.current.open(map);
          }
          if (onHotelSelect) onHotelSelect(h.hotel_id);
        },
        onHover: (id) => {
          if (onHotelHover) onHotelHover(id);
        }
      });
      overlay.setMap(map);
      bounds.extend({ lat, lng });
      overlaysRef.current.push(overlay);
    });

    // Nearby hotel markers (gray circles, no price)
    validNearby.forEach(hotel => {
      const lat = parseFloat(hotel.latitude);
      const lng = parseFloat(hotel.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map,
        title: hotel.name,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: '#94a3b8',
          fillOpacity: 0.7,
          strokeColor: '#fff',
          strokeWeight: 1.5
        },
        zIndex: 1
      });

      if (showInfoWindows) {
        marker.addListener('click', () => {
          infoWindowRef.current.setContent(`
            <div style="padding:4px 2px;max-width:200px;font-family:system-ui">
              <div style="font-size:11px;color:#94a3b8;margin-bottom:1px">Nearby</div>
              <div style="font-weight:600;font-size:13px">${hotel.name}</div>
            </div>
          `);
          infoWindowRef.current.open(map, marker);
        });
      }

      bounds.extend({ lat, lng });
      nearbyMarkersRef.current.push(marker);
    });

    // User location marker (blue pulsing dot)
    if (userLocation && userLocation.lat && userLocation.lng) {
      if (userMarkerRef.current) userMarkerRef.current.setMap(null);
      const userMarker = new window.google.maps.Marker({
        position: { lat: userLocation.lat, lng: userLocation.lng },
        map,
        title: 'Your location',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#3b82f6',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 3
        },
        zIndex: 200
      });
      userMarkerRef.current = userMarker;
      bounds.extend({ lat: userLocation.lat, lng: userLocation.lng });
    }

    // Fit bounds
    if (validHotels.length > 1 || validNearby.length > 0) {
      map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
    } else if (validHotels.length === 1) {
      map.setCenter({ lat: parseFloat(validHotels[0].latitude), lng: parseFloat(validHotels[0].longitude) });
      map.setZoom(14);
    }
  }, [mapReady, validHotels, validNearby, showInfoWindows, userLocation]);

  // Highlight selected overlay
  useEffect(() => {
    if (!mapReady) return;
    overlaysRef.current.forEach(overlay => {
      overlay.updateSelected(selectedHotelId);
    });
  }, [selectedHotelId, mapReady]);

  if (validHotels.length === 0) return null;

  return (
    <div className={`rounded-xl overflow-hidden border border-border ${className}`} style={{ height }} data-testid="hotel-map">
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default HotelMap;
